import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import InfoChip from './InfoChip';
import './LeaseSigning.css';

const formatCurrency = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-CA');
};

const statusColors = {
  draft: '#6b7280',
  sent: '#2f6fed',
  accepted: '#059669',
  declined: '#ef4444',
  countered: '#f59e0b',
  activated: '#059669',
  cancelled: '#6b7280'
};

function TenantLease() {
  const { user, authReady } = useContext(AuthContext);
  const navigate = useNavigate();
  const [lease, setLease] = useState(null);
  const [leases, setLeases] = useState([]);
  const [renewal, setRenewal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [leaseUnread, setLeaseUnread] = useState(0);
  const [hideNewItems, setHideNewItems] = useState(localStorage.getItem('dismiss_newitems_tenant_lease') === '1');

  // E-sign state
  const [esignModalUrl, setEsignModalUrl] = useState('');
  const [esignLoading, setEsignLoading] = useState(false);
  const [esignError, setEsignError] = useState('');

  // Renewal action state
  const [renewalAction, setRenewalAction] = useState(null); // 'accept' | 'decline' | 'counter'
  const [counterRent, setCounterRent] = useState('');
  const [counterNote, setCounterNote] = useState('');
  const [declineNote, setDeclineNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    const fetchLease = async () => {
      if (!authReady) return;
      if (!user || user.userType !== 'tenant') {
        navigate('/login');
        return;
      }
      try {
        const [leaseRes, leasesRes, renewalRes, countRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/leases/latest`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/leases`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/lease-renewals/latest`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/lease/unread-count`, {
            headers: { Authorization: `Bearer ${user.token}` }
          })
        ]);
        const leaseData = leaseRes.ok ? await leaseRes.json() : { lease: null };
        const leasesData = leasesRes.ok ? await leasesRes.json() : { leases: [] };
        const renewalData = renewalRes.ok ? await renewalRes.json() : { renewal: null };
        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setLastViewedAt(countData.last_viewed_at || null);
          setLeaseUnread(countData.count || 0);
        }
        setLease(leaseData.lease || null);
        setLeases(leasesData.leases || []);
        setRenewal(renewalData.renewal || null);
      } catch (err) {
        setError(err.message || 'Could not load your lease.');
      } finally {
        setLoading(false);
      }
    };
    fetchLease();
  }, [user, authReady, navigate]);

  useEffect(() => {
    const markViewed = async () => {
      if (!user || !user.token) return;
      try {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/lease/mark-viewed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${user.token}` }
        });
        window.dispatchEvent(new Event('lease:viewed'));
      } catch {}
    };
    markViewed();
  }, [user]);

  // Find lease pending tenant signature
  const pendingSignLease = leases.find(l => ['sent', 'landlord_signed'].includes(l.status));

  const handleSignLease = useCallback(async () => {
    if (!pendingSignLease || !user?.token) return;
    setEsignLoading(true);
    setEsignError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/leases/${pendingSignLease.id}/esign/url?role=tenant`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to get signing link');
      if (data.url) {
        if (window.location.protocol === 'http:' || window.location.hostname.includes('192.168.')) {
          window.open(data.url, '_blank');
        } else {
          setEsignModalUrl(data.url);
        }
      } else {
        throw new Error('Signing link not available yet. Please try again later.');
      }
    } catch (err) {
      setEsignError(err.message || 'Failed to open signing link');
    } finally {
      setEsignLoading(false);
    }
  }, [pendingSignLease, user]);

  // Listen for BoldSign iframe postMessage events
  useEffect(() => {
    if (!esignModalUrl) return;
    const handler = (event) => {
      const action = event.data?.action || (typeof event.data === 'string' ? event.data : '');
      if (['onDocumentSigned', 'onDocumentDeclined', 'onDocumentReassigned'].includes(action)) {
        setEsignModalUrl('');
        window.location.reload();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [esignModalUrl]);

  const handleRenewalAction = async (action) => {
    if (!renewal || actionLoading) return;
    setActionLoading(true);
    setActionMessage('');

    try {
      let url, body;
      if (action === 'accept') {
        url = `${process.env.REACT_APP_API_BASE_URL}/api/tenants/lease-renewals/${renewal.id}/accept`;
        body = {};
      } else if (action === 'decline') {
        url = `${process.env.REACT_APP_API_BASE_URL}/api/tenants/lease-renewals/${renewal.id}/decline`;
        body = { note: declineNote || undefined };
      } else if (action === 'counter') {
        if (!counterRent || Number(counterRent) <= 0) {
          setActionMessage('Please enter a valid counter rent amount.');
          setActionLoading(false);
          return;
        }
        url = `${process.env.REACT_APP_API_BASE_URL}/api/tenants/lease-renewals/${renewal.id}/counter`;
        body = {
          proposed_rent: Number(counterRent),
          note: counterNote || undefined
        };
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data.error || `Failed to ${action} renewal`);

      setRenewal(data.renewal || { ...renewal, status: action === 'accept' ? 'accepted' : action === 'decline' ? 'declined' : 'countered' });
      setRenewalAction(null);
      setActionMessage(
        action === 'accept' ? 'Renewal accepted! Your landlord will finalize the new lease.' :
        action === 'decline' ? 'Renewal declined. Your landlord has been notified.' :
        'Counter offer sent! Your landlord will review it.'
      );
    } catch (err) {
      setActionMessage(err.message || 'Something went wrong.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <main className="App-main">Loading lease...</main>;

  const handleLeaseDownload = (leaseId) => {
    if (!leaseId) return;
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/tenants/leases/${leaseId}/download${token ? `?token=${token}` : ''}`;
    window.open(url, '_blank');
  };

  const sortedLeases = leases.slice().sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
  const hasSignedLease = sortedLeases.some((item) => ['signed', 'tenant_signed', 'completed'].includes(item.status));
  const filteredLeases = hasSignedLease
    ? sortedLeases.filter((item) => ['signed', 'tenant_signed', 'completed'].includes(item.status))
    : sortedLeases;
  const displayLeases = filteredLeases.slice(0, 1);

  const renewalPending = renewal && renewal.status === 'sent';
  const renewalCountered = renewal && renewal.status === 'countered';

  // Lease expiry info
  const currentLease = leases.find(l => l.status === 'signed' || l.status === 'completed' || l.status === 'tenant_signed');
  const leaseEndDate = currentLease?.lease_end_date;
  const daysUntilExpiry = leaseEndDate ? Math.ceil((new Date(leaseEndDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;

  const latestItems = [
    lease && {
      id: 'lease',
      title: lease.status === 'signed' ? 'Signed lease' : 'Lease document',
      subtitle: lease.original_filename || 'Lease document',
      timestamp: lease.updated_at || lease.created_at,
      link: '#lease-section'
    },
    renewal && {
      id: 'renewal',
      title: `Renewal ${renewal.status || 'update'}`,
      subtitle: renewal.proposed_start_date ? `Starts ${formatDate(renewal.proposed_start_date)}` : 'Renewal update',
      timestamp: renewal.updated_at || renewal.created_at,
      link: '#renewal-section'
    }
  ].filter(Boolean).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 3);

  return (
    <main className="App-main tenants-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Lease</p>
          <h2>Lease {leaseUnread > 0 && <span className="nav-badge">{leaseUnread}</span>}</h2>
          <p>Review your active lease and respond to renewal offers.</p>
          <InfoChip label="Lease tips">
            Download the signed PDF anytime—if a renewal arrives, it'll appear below with Accept/Decline buttons.
          </InfoChip>
          {error && <p className="error-message">{error}</p>}
        </div>
      </div>

      {/* Lease expiry warning */}
      {daysUntilExpiry !== null && daysUntilExpiry <= 90 && daysUntilExpiry > 0 && !renewal && (
        <div className="dashboard-section" style={{ borderLeft: `4px solid ${daysUntilExpiry <= 30 ? '#ef4444' : '#f59e0b'}`, background: daysUntilExpiry <= 30 ? '#fff5f5' : '#fff8e1', padding: '0.75rem', marginBottom: '1rem' }}>
          <p style={{ margin: 0, fontWeight: '600' }}>
            {daysUntilExpiry <= 30 ? '⚠️' : '📅'} Your lease expires {formatDate(leaseEndDate)} ({daysUntilExpiry} days)
          </p>
          <p className="status-text" style={{ margin: '0.25rem 0 0' }}>
            {daysUntilExpiry <= 30
              ? 'Contact your landlord about renewal if you haven\'t already.'
              : 'Your landlord may send a renewal offer soon.'}
          </p>
        </div>
      )}

      {daysUntilExpiry !== null && daysUntilExpiry <= 0 && !renewal && (
        <div className="dashboard-section" style={{ borderLeft: '4px solid #ef4444', background: '#fff5f5', padding: '0.75rem', marginBottom: '1rem' }}>
          <p style={{ margin: 0, fontWeight: '600' }}>⚠️ Your lease expired on {formatDate(leaseEndDate)}</p>
          <p className="status-text" style={{ margin: '0.25rem 0 0' }}>
            Contact your landlord about renewing or transitioning to month-to-month.
          </p>
        </div>
      )}

      {/* New items */}
      {!hideNewItems && latestItems.length > 0 && (
        <div className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff' }}>
          <div className="section-header">
            <h3>New items</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="report-chip report-chip--muted">Latest activity</span>
              <button className="card-dismiss" onClick={() => { localStorage.setItem('dismiss_newitems_tenant_lease', '1'); setHideNewItems(true); }} aria-label="Dismiss">Dismiss</button>
            </div>
          </div>
          <div className="inquiry-list">
            {latestItems.map((item) => {
              const isNew = lastViewedAt && new Date(item.timestamp) > new Date(lastViewedAt);
              return (
                <div key={item.id} className="inquiry-card">
                  <p><strong>{item.title}</strong></p>
                  <p className="status-text">{item.subtitle}</p>
                  <p className="timestamp">{new Date(item.timestamp).toLocaleString()}</p>
                  <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                    <a className="secondary-button" href={item.link}>View</a>
                    {isNew && <span className="report-chip" style={{ marginLeft: '0.5rem' }}>New</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sign Your Lease banner */}
      {/* Sign or view lease */}
      {pendingSignLease && !hasSignedLease && (
        <div className="dashboard-section" style={{ borderLeft: '4px solid #2f6fed', background: '#f0f5ff', padding: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 0.35rem', color: '#1e40af' }}>✍️ Your Lease is Ready to Sign</h3>
          <p className="status-text" style={{ margin: '0 0 0.75rem' }}>
            Your landlord has prepared the lease. Review and sign it electronically.
          </p>
          <button
            className="primary-button"
            type="button"
            onClick={handleSignLease}
            disabled={esignLoading}
          >
            {esignLoading ? 'Opening…' : 'Sign Lease Now'}
          </button>
          {esignError && <p className="error-message" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>{esignError}</p>}
        </div>
      )}

      {/* E-sign iframe modal */}
      {esignModalUrl && (
        <div className="esign-modal-overlay" onClick={() => setEsignModalUrl('')}>
          <div className="esign-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="esign-modal-header">
              <span style={{ fontWeight: 600 }}>Sign Your Lease</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button type="button" className="success-button" style={{ fontSize: '0.82rem', padding: '0.35rem 0.85rem' }} onClick={() => { setEsignModalUrl(''); window.location.reload(); }}>
                  ✅ Done Signing
                </button>
                <button type="button" className="ghost-button" onClick={() => setEsignModalUrl('')}>✕</button>
              </div>
            </div>
            <iframe src={esignModalUrl} title="E-Sign Lease" className="esign-modal-iframe" />
          </div>
        </div>
      )}

      {/* Your Lease */}
      <div className="dashboard-section" id="lease-section">
        <h3>Your Lease</h3>
        {displayLeases.length > 0 ? (
          <>
            {hasSignedLease && (
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem', color: '#15803d', fontWeight: 600 }}>✅ Fully signed</p>
              </div>
            )}
            <button className="primary-button" type="button" style={{ marginBottom: '0.75rem' }} onClick={() => handleLeaseDownload(displayLeases[0].id)}>
              📄 Download {hasSignedLease ? 'Signed ' : ''}Lease
            </button>
            {currentLease && (
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {currentLease.lease_start_date && (
                  <p className="status-text"><strong>Start:</strong> {formatDate(currentLease.lease_start_date)}</p>
                )}
                {currentLease.lease_end_date && (
                  <p className="status-text"><strong>End:</strong> {formatDate(currentLease.lease_end_date)}</p>
                )}
                {currentLease.monthly_rent && (
                  <p className="status-text"><strong>Rent:</strong> {formatCurrency(currentLease.monthly_rent)}/mo</p>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="status-text">No lease available yet.</p>
        )}
      </div>

      {/* Lease Renewal */}
      {renewal && (
        <div className="dashboard-section" id="renewal-section" style={{ borderLeft: `4px solid ${statusColors[renewal.status] || '#6b7280'}` }}>
          <div className="section-header" style={{ alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Lease Renewal</h3>
            <span className={`status-pill ${renewal.status}`} style={{ backgroundColor: statusColors[renewal.status], color: '#fff' }}>
              {renewal.status === 'sent' ? 'Action required' : renewal.status}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '0.75rem 0' }}>
            <div>
              <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Proposed Rent</p>
              <p style={{ margin: 0, fontWeight: '700', fontSize: '1.1rem' }}>{formatCurrency(renewal.proposed_rent)}/mo</p>
            </div>
            <div>
              <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Start Date</p>
              <p style={{ margin: 0, fontWeight: '600' }}>{formatDate(renewal.proposed_start_date)}</p>
            </div>
            <div>
              <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>End Date</p>
              <p style={{ margin: 0, fontWeight: '600' }}>{formatDate(renewal.proposed_end_date)}</p>
            </div>
            {renewal.proposed_rent && currentLease?.monthly_rent && Number(renewal.proposed_rent) !== Number(currentLease.monthly_rent) && (
              <div>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Change</p>
                <p style={{ margin: 0, fontWeight: '600', color: Number(renewal.proposed_rent) > Number(currentLease.monthly_rent) ? '#ef4444' : '#059669' }}>
                  {Number(renewal.proposed_rent) > Number(currentLease.monthly_rent) ? '+' : ''}
                  {formatCurrency(Number(renewal.proposed_rent) - Number(currentLease.monthly_rent))}
                </p>
              </div>
            )}
          </div>

          {renewal.note && (
            <div style={{ background: '#f8f9fa', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
              <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Note from landlord</p>
              <p style={{ margin: '0.25rem 0 0' }}>{renewal.note}</p>
            </div>
          )}

          {renewal.counter_rent && (
            <div style={{ background: '#fffbeb', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
              <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Your counter offer</p>
              <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>{formatCurrency(renewal.counter_rent)}/mo</p>
              {renewal.counter_note && <p className="status-text" style={{ margin: '0.25rem 0 0' }}>{renewal.counter_note}</p>}
            </div>
          )}

          {actionMessage && (
            <div className="dashboard-section" style={{ padding: '0.5rem 0.75rem', borderLeft: '3px solid #059669', background: '#f0fff4', marginBottom: '0.75rem' }}>
              <p style={{ margin: 0 }}>{actionMessage}</p>
            </div>
          )}

          {/* Action buttons — only show when renewal is pending */}
          {renewalPending && !renewalAction && (
            <div className="form-actions" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="primary-button" onClick={() => {
                if (window.confirm(`Accept renewal at ${formatCurrency(renewal.proposed_rent)}/mo starting ${formatDate(renewal.proposed_start_date)}?`)) {
                  handleRenewalAction('accept');
                }
              }} disabled={actionLoading}>
                {actionLoading ? 'Processing…' : '✓ Accept'}
              </button>
              <button className="secondary-button" onClick={() => { setRenewalAction('counter'); setCounterRent(renewal.proposed_rent || ''); }} disabled={actionLoading}>
                Counter Offer
              </button>
              <button className="secondary-button" onClick={() => setRenewalAction('decline')} disabled={actionLoading} style={{ color: '#ef4444', borderColor: '#ef4444' }}>
                Decline
              </button>
            </div>
          )}

          {/* Counter offer form */}
          {renewalAction === 'counter' && (
            <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '6px', marginTop: '0.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem' }}>Counter Offer</h4>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label className="status-text" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Your proposed rent (CAD/month)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={counterRent}
                    onChange={(e) => setCounterRent(e.target.value)}
                    min="0"
                    step="1"
                    placeholder="e.g. 1800"
                  />
                </div>
              </div>
              <label className="status-text" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Note (optional)</label>
              <textarea
                className="form-control"
                value={counterNote}
                onChange={(e) => setCounterNote(e.target.value)}
                rows={2}
                placeholder="Explain your counter offer…"
                style={{ width: '100%', resize: 'vertical' }}
              />
              <div className="form-actions" style={{ marginTop: '0.5rem', gap: '0.5rem' }}>
                <button className="primary-button" onClick={() => handleRenewalAction('counter')} disabled={actionLoading}>
                  {actionLoading ? 'Sending…' : 'Send Counter'}
                </button>
                <button className="secondary-button" onClick={() => setRenewalAction(null)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Decline form */}
          {renewalAction === 'decline' && (
            <div style={{ background: '#fff5f5', padding: '0.75rem', borderRadius: '6px', marginTop: '0.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem' }}>Decline Renewal</h4>
              <p className="status-text" style={{ marginBottom: '0.5rem' }}>Are you sure? Your landlord will be notified.</p>
              <label className="status-text" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Reason (optional)</label>
              <textarea
                className="form-control"
                value={declineNote}
                onChange={(e) => setDeclineNote(e.target.value)}
                rows={2}
                placeholder="Let your landlord know why…"
                style={{ width: '100%', resize: 'vertical' }}
              />
              <div className="form-actions" style={{ marginTop: '0.5rem', gap: '0.5rem' }}>
                <button className="primary-button" onClick={() => handleRenewalAction('decline')} disabled={actionLoading} style={{ background: '#ef4444' }}>
                  {actionLoading ? 'Processing…' : 'Confirm Decline'}
                </button>
                <button className="secondary-button" onClick={() => setRenewalAction(null)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Countered status — waiting for landlord response */}
          {renewalCountered && (
            <div style={{ background: '#fffbeb', padding: '0.5rem 0.75rem', borderRadius: '6px', marginTop: '0.5rem' }}>
              <p style={{ margin: 0 }}>⏳ Waiting for your landlord to respond to your counter offer.</p>
            </div>
          )}

          {/* Final statuses */}
          {renewal.status === 'accepted' && (
            <div style={{ background: '#f0fff4', padding: '0.5rem 0.75rem', borderRadius: '6px', marginTop: '0.5rem' }}>
              <p style={{ margin: 0 }}>✅ You accepted this renewal. Your landlord will prepare the new lease.</p>
            </div>
          )}

          {renewal.status === 'activated' && (
            <div style={{ background: '#f0fff4', padding: '0.5rem 0.75rem', borderRadius: '6px', marginTop: '0.5rem' }}>
              <p style={{ margin: 0 }}>✅ Renewal is active. Your new lease terms have been applied.</p>
            </div>
          )}

          {renewal.status === 'declined' && (
            <div style={{ background: '#f9f9f9', padding: '0.5rem 0.75rem', borderRadius: '6px', marginTop: '0.5rem' }}>
              <p style={{ margin: 0 }}>You declined this renewal offer.</p>
            </div>
          )}

          {renewal.status === 'cancelled' && (
            <div style={{ background: '#f9f9f9', padding: '0.5rem 0.75rem', borderRadius: '6px', marginTop: '0.5rem' }}>
              <p style={{ margin: 0 }}>This renewal was cancelled by your landlord.</p>
            </div>
          )}

          <p className="timestamp" style={{ marginTop: '0.5rem' }}>
            Last updated {new Date(renewal.updated_at || renewal.created_at).toLocaleString()}
          </p>
        </div>
      )}

      {/* No renewal section */}
      {!renewal && !loading && (
        <div className="dashboard-section" id="renewal-section">
          <h3>Lease Renewal</h3>
          <p className="status-text">No active renewal offer. When your landlord sends one, it will appear here.</p>
        </div>
      )}
    </main>
  );
}

export default TenantLease;
